import { client } from '../api/client';
import { AxiosResponse } from 'axios';
import { CallbackEvent } from '../types/sdk';
import { CompactEncrypt, importJWK, JWK } from 'jose';
import { ContentType, HttpHeader } from '../constants/http';
import {
	BankAuthenticationSuccessData,
	ChooseAccountData,
	ChooseBankData,
	FormData,
	FormSubmission,
	ISessionService,
	SessionConfiguration
} from './interfaces/session.interface';
import { Session, SessionState } from '../types/session';

class SessionService implements ISessionService {
	async createSession(configuration: SessionConfiguration): Promise<Session> {
		const response: AxiosResponse<Session> = await client.post('/session', configuration);
		return response.data;
	}

	async getSession(): Promise<Session> {
		const response: AxiosResponse<Session> = await client.get('/session');
		return response.data;
	}

	async getSessionState(sessionId: string): Promise<SessionState> {
		const path = this.replace('/session/:id', sessionId);
		const response = await client.get(path);
		return response.data;
	}

	async authenticated(sessionId: string, data: BankAuthenticationSuccessData): Promise<SessionState> {
		const path = this.replace('/session/:id/auth_completed', sessionId);
		const response = await client.put(path, data, {});
		return response.data;
	}

	async chooseBank(sessionId: string, data: ChooseBankData): Promise<SessionState> {
		const path = this.replace('/session/:id/bank', sessionId);
		const response = await client.put(path, data, {});
		return response.data;
	}

	async submitForm(sessionId: string, formId: string, data: FormData, key: JWK): Promise<SessionState> {
		const path = this.replace('/session/:id/form', sessionId);
		const form = this.createFormSubmission(formId, data);
		const encrypted = await this.encrypt(form, key);

		const response = await client.put(path, encrypted, {
			headers: {
				[HttpHeader['Content-Type']]: ContentType['application/jose']
			}
		});

		return response.data;
	}

	async chooseAccount(sessionId: string, data: ChooseAccountData): Promise<SessionState> {
		const path = this.replace('/session/:id/account', sessionId);
		const response = await client.put(path, data, {});
		return response.data;
	}

	async logEvent(sessionId: string, event: CallbackEvent, data: any = {}): Promise<void> {
		const path = this.replace('/session/:id/callback', sessionId);
		await client.put(path, {
			callbackType: event,
			data
		});
	}

	private async encrypt(form: FormSubmission, key: JWK): Promise<string> {
		const encoder = new TextEncoder();
		const data = encoder.encode(JSON.stringify(form));
		const cryptoKey = await importJWK(key, 'ECDH-ES');
		return await new CompactEncrypt(data).setProtectedHeader({ alg: 'ECDH-ES', enc: 'A256GCM' }).encrypt(cryptoKey);
	}

	private createFormSubmission(id: string, data: FormData): FormSubmission {
		return {
			form_identifier: id,
			data
		};
	}

	private replace(path: string, id: string): string {
		return path.replace(':id', id);
	}
}

export const sessionService = new SessionService();

export default sessionService;
